import { useEffect, useRef } from "react";
import { useOverlayStackContext } from "../context/overlay-stack-context";

interface UseOverlayStackOptions {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  level?: 1 | 2;
}

/**
 * Registers an overlay with the global overlay stack.
 * Handles Escape key delegation and focus restoration.
 */
export function useOverlayStack({
  id,
  isOpen,
  onClose,
  level = 1,
}: UseOverlayStackOptions) {
  const { push, pop, isTopmost, closeTopmost } = useOverlayStackContext();
  const triggerRef = useRef<HTMLElement | null>(null);

  // Capture the trigger element (the element focused before overlay opens)
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
      push({
        id,
        triggerRef: triggerRef as React.RefObject<HTMLElement | null>,
        close: onClose,
        level,
      });
    } else {
      pop(id);
    }
  }, [isOpen]);

  // Global Escape handler — only the topmost overlay responds
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isTopmost(id)) {
        e.preventDefault();
        e.stopPropagation();
        closeTopmost();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, id, isTopmost, closeTopmost]);

  return { triggerRef };
}
